/** @format */
import {
    HeadingColor,
    HighlightColor,
} from '@mpb-com/feather/components/atoms/heading/types';
import { useTranslation } from 'next-i18next';
import React, { Fragment, FunctionComponent } from 'react';
import styled from 'styled-components';

import {
    ConstrainedWrapper,
    ConstrainedWrapperGutter,
} from '@toucan/client/shared/components/constrained-wrapper';

import { BlockTitle } from '../block-title';
import { CategoryTile, CategoryTileSkeleton } from '../category-tile';
import { styles } from './category-block.style';
import {
    CategoryBlockProps,
    CategoryBlockVariant,
    StyledCategoryBlockProps,
} from './types';

const StyledCategoryBlock = styled.section<StyledCategoryBlockProps>`
    ${styles}
`;

const CategoryBlockSkeleton: FunctionComponent = () => {
    return (
        <Fragment>
            <BlockTitle showPretitle isLoading />

            <div className="category-tiles">
                <CategoryTileSkeleton data-testid="skeleton-tile" />
                <CategoryTileSkeleton data-testid="skeleton-tile" />
                <CategoryTileSkeleton data-testid="skeleton-tile" />
                <CategoryTileSkeleton data-testid="skeleton-tile" />
            </div>
        </Fragment>
    );
};

const getVariantHeadingColors = (variant?: CategoryBlockVariant) =>
    variant === CategoryBlockVariant.MIDNIGHT
        ? {
              headingColor: HeadingColor.TEXT_INVERSE,
              highlightColor: HighlightColor.TEXT_ACCENT,
          }
        : {
              headingColor: HeadingColor.TEXT_SECONDARY,
              highlightColor: HighlightColor.TEXT_ACCENT_INVERSE,
          };

export const CategoryBlock: FunctionComponent<CategoryBlockProps> = ({
    isLoading,
    tiles,
    variant,
}) => {
    const { t } = useTranslation();
    const { headingColor, highlightColor } = getVariantHeadingColors(variant);

    const view = () => {
        if (isLoading) {
            return <CategoryBlockSkeleton />;
        }

        return (
            <Fragment>
                <BlockTitle
                    title={t('Get **inspired**')}
                    pretitle={t('Explore our categories')}
                    showPretitle
                    headingColor={headingColor}
                    highlightColor={highlightColor}
                />
                <div className="category-tiles">
                    {tiles?.map((tile, index) => (
                        <CategoryTile
                            {...tile}
                            titleColor={headingColor}
                            key={index}
                        />
                    ))}
                </div>
            </Fragment>
        );
    };

    return (
        <StyledCategoryBlock variant={variant}>
            <ConstrainedWrapper
                className="wrapper"
                gutter={ConstrainedWrapperGutter.LARGE_GUTTER}
            >
                {view()}
            </ConstrainedWrapper>
        </StyledCategoryBlock>
    );
};
